import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Phone,
  UserCheck,
  UserX,
  AlertCircle,
  X
} from 'lucide-react';
import {
  getEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  toggleEmployeeStatus,
  getRestaurantProfile,
  handleRestaurantApiError
} from '../../../services/restaurantApi';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const ROLE_BADGE = {
  manager: { bg: '#F1F8E8', fg: '#37702C' },
  kitchen: { bg: '#FEF0EA', fg: '#A32C0F' },
  service: { bg: '#FEF6DD', fg: '#8A6A00' },
  delivery: { bg: '#DDEECB', fg: '#054625' },
  cashier: { bg: '#F4F1E2', fg: '#393A30' },
  staff: { bg: '#F4F1E2', fg: '#393A30' },
};

const STATUS_BADGE = {
  active: { bg: '#DDEECB', fg: '#054625', label: 'Actif' },
  inactive: { bg: '#F4F1E2', fg: '#393A30', label: 'Inactif' },
  suspended: { bg: '#FEF0EA', fg: '#A32C0F', label: 'Suspendu' },
};

const inputClass =
  'w-full min-h-[44px] px-4 bg-[#FBF9D9] border-none rounded-full focus:outline-none focus:ring-2 focus:ring-[#054625]/30 text-[#051004] text-sm';

const RestaurantEmployees = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [showAddModal, setShowAddModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', phone: '', role: 'staff', hire_date: '', salary: '' });
  const [updating, setUpdating] = useState(false);
  const [addForm, setAddForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'staff',
    hire_date: new Date().toISOString().slice(0, 10),
    salary: ''
  });

  // Rôles alignés avec l'API : manager, kitchen, service, cashier, delivery, staff
  const roles = [
    { id: 'all', name: 'Tous les rôles' },
    { id: 'manager', name: 'Gestionnaire' },
    { id: 'kitchen', name: 'Chef de cuisine' },
    { id: 'service', name: 'Serveur' },
    { id: 'cashier', name: 'Caissier' },
    { id: 'delivery', name: 'Livreur' },
    { id: 'staff', name: 'Personnel' }
  ];

  useEffect(() => {
    // Charger les vraies données des employés
    const loadEmployees = async () => {
      setLoading(true);
      setError(null);

      try {
        const employeesData = await getEmployees();
        const list = Array.isArray(employeesData) ? employeesData : (employeesData?.employees || []);
        setEmployees(list);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des employés:', error);
        setError(handleRestaurantApiError(error));
        setLoading(false);
      }
    };

    loadEmployees();
  }, []);

  // Normaliser un employé pour l'affichage (éviter NaN sur salary / hoursPerWeek)
  const normalizeEmployee = (e) => ({
    ...e,
    name: e.name || [e.first_name, e.last_name].filter(Boolean).join(' ') || '—',
    status: e.status ?? (e.is_active === false ? 'inactive' : 'active'),
    hireDate: e.hireDate || e.hire_date,
    hoursPerWeek: Number(e.hoursPerWeek) || 0,
    salary: Number(e.salary) || 0
  });

  // Fonction pour filtrer les employés
  const filterEmployees = () => {
    let filtered = employees.map(normalizeEmployee);

    // Filtrer par terme de recherche
    if (searchTerm) {
      filtered = filtered.filter(employee =>
        employee.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrer par rôle
    if (filterRole !== 'all') {
      filtered = filtered.filter(employee => employee.role === filterRole);
    }

    if (statusFilter === 'active') {
      filtered = filtered.filter(employee => employee.status === 'active');
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(employee => employee.status === 'inactive');
    } else if (statusFilter === 'manager') {
      filtered = filtered.filter(employee => employee.role === 'manager');
    }

    setFilteredEmployees(filtered);
  };

  // Appliquer les filtres quand les données ou les filtres changent
  useEffect(() => {
    filterEmployees();
  }, [employees, searchTerm, filterRole, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRole, statusFilter]);

  const activeCount = employees.filter(e => (e.status === 'active' || e.is_active !== false)).length;
  const inactiveCount = Math.max(0, employees.length - activeCount);
  const managerCount = employees.filter(e => e.role === 'manager').length;
  const totalPages = Math.max(1, Math.ceil(filteredEmployees.length / pageSize));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedEmployees = filteredEmployees.slice(
    (currentPageSafe - 1) * pageSize,
    currentPageSafe * pageSize,
  );

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : roleId;
  };

  const formatHireDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('fr-FR');
  };

  // Fonction pour créer un nouvel employé (payload attendu par l'API : restaurant_id, email, password, first_name, last_name, phone, role, hire_date, salary, permissions)
  const handleCreateEmployee = async (formData) => {
    setCreateError(null);
    setCreating(true);
    try {
      const profile = await getRestaurantProfile();
      const restaurantId = profile?.id ?? profile?.restaurant_id;
      if (!restaurantId) {
        setCreateError('Impossible d\'identifier le restaurant. Reconnectez-vous.');
        setCreating(false);
        return;
      }
      const hireDate = formData.hire_date || formData.hireDate;
      const payload = {
        restaurant_id: restaurantId,
        email: formData.email.trim(),
        password: formData.password,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        phone: formData.phone?.trim() || null,
        role: formData.role,
        hire_date: typeof hireDate === 'string' ? hireDate : (hireDate ? hireDate.toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10)),
        salary: formData.salary != null && formData.salary !== '' ? parseFloat(formData.salary) : null,
        permissions: {}
      };
      const newEmployee = await createEmployee(payload);
      setEmployees([...employees, newEmployee]);
      setShowAddModal(false);
    } catch (err) {
      console.error('Erreur lors de la création de l\'employé:', err);
      let msg = handleRestaurantApiError(err);
      if (typeof msg !== 'string') msg = err?.response?.data?.detail;
      if (Array.isArray(msg)) msg = msg.map((m) => m.msg || m.loc?.join('.') || JSON.stringify(m)).join(' ; ');
      if (typeof msg !== 'string') msg = 'Impossible d\'ajouter l\'employé.';
      setCreateError(msg);
    } finally {
      setCreating(false);
    }
  };

  // Ouvrir le modal d'édition avec les données de l'employé
  const openEditModal = (emp) => {
    const e = normalizeEmployee(emp);
    setEditingEmployee(emp);
    setEditForm({
      first_name: emp.first_name ?? e.name?.split(' ')[0] ?? '',
      last_name: emp.last_name ?? e.name?.split(' ').slice(1).join(' ') ?? '',
      phone: emp.phone ?? e.phone ?? '',
      role: emp.role ?? e.role ?? 'staff',
      hire_date: (emp.hire_date || e.hireDate) ? (typeof emp.hire_date === 'string' ? emp.hire_date : (e.hireDate ? new Date(e.hireDate).toISOString().slice(0, 10) : '')) : new Date().toISOString().slice(0, 10),
      salary: emp.salary ?? e.salary ?? ''
    });
    setError(null);
  };

  // Fonction pour mettre à jour un employé
  const handleUpdateEmployee = async (employeeId, employeeData) => {
    try {
      setError(null);
      setUpdating(true);
      const payload = {
        first_name: employeeData.first_name,
        last_name: employeeData.last_name,
        phone: employeeData.phone || null,
        role: employeeData.role,
        hire_date: employeeData.hire_date,
        salary: employeeData.salary ? parseFloat(employeeData.salary) : null
      };
      const updatedEmployee = await updateEmployee(employeeId, payload);
      setEmployees(employees.map(employee =>
        employee.id === employeeId ? { ...employee, ...updatedEmployee } : employee
      ));
      setEditingEmployee(null);
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'employé:', error);
      setError(typeof error === 'string' ? error : handleRestaurantApiError(error));
    } finally {
      setUpdating(false);
    }
  };

  // Fonction pour supprimer un employé
  const handleDeleteEmployee = async (employeeId) => {
    try {
      setError(null);
      await deleteEmployee(employeeId);
      setEmployees(employees.filter(employee => employee.id !== employeeId));
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'employé:', error);
      setError(typeof error === 'string' ? error : handleRestaurantApiError(error));
    }
  };

  // Fonction pour basculer le statut d'un employé
  const handleToggleEmployeeStatus = async (employeeId) => {
    try {
      setError(null);
      const updated = await toggleEmployeeStatus(employeeId);
      setEmployees(employees.map(employee =>
        employee.id === employeeId
          ? { ...employee, is_active: updated.is_active, status: updated.is_active ? 'active' : 'inactive' }
          : employee
      ));
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      setError(typeof error === 'string' ? error : handleRestaurantApiError(error));
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 'clamp(20px, 2.6vw, 34px) clamp(18px, 2.6vw, 36px)' }}>
        <div className="animate-pulse" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ height: '32px', width: '220px', borderRadius: '999px', background: '#F4F1E2' }} />
          {[...Array(6)].map((_, i) => (
            <div key={i} style={{ background: '#FCFBF5', borderRadius: '24px', padding: '16px' }} className="flex items-center gap-4">
              <div style={{ width: '48px', height: '48px', borderRadius: '999px', background: '#F4F1E2' }} />
              <div className="flex-1" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ height: '14px', width: '25%', borderRadius: '999px', background: '#F4F1E2' }} />
                <div style={{ height: '12px', width: '45%', borderRadius: '999px', background: '#F4F1E2' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 'clamp(20px, 2.6vw, 34px) clamp(18px, 2.6vw, 36px) clamp(36px, 4vw, 60px)', display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 2vw, 24px)' }}>
      {/* En-tête */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: TERRA }}>
            {employees.length} employé{employees.length > 1 ? 's' : ''}
          </span>
          <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 3vw, 38px)', lineHeight: 1, letterSpacing: '-0.025em' }}>Employés</h1>
        </div>
        <button
          onClick={() => {
            setCreateError(null);
            setAddForm({
              email: '',
              password: '',
              first_name: '',
              last_name: '',
              phone: '',
              role: 'staff',
              hire_date: new Date().toISOString().slice(0, 10),
              salary: ''
            });
            setShowAddModal(true);
          }}
          className="inline-flex items-center gap-2"
          style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', padding: '0 20px', borderRadius: '999px' }}
        >
          <Plus size={16} />
          Ajouter un employé
        </button>
      </div>

      {/* Modal Ajouter un employé */}
      {showAddModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(5,16,4,0.5)' }}>
          <div style={{ background: '#FCFBF5', borderRadius: '28px', maxWidth: '460px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(5,16,4,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '22px 26px', borderBottom: '1px solid rgba(5,16,4,0.08)' }}>
              <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>Nouvel employé</h2>
              <button
                type="button"
                onClick={() => !creating && setShowAddModal(false)}
                style={{ flexShrink: 0, border: 'none', cursor: 'pointer', width: '36px', height: '36px', borderRadius: '999px', background: 'transparent', color: 'rgba(5,16,4,0.4)', display: 'grid', placeItems: 'center' }}
              >
                <X size={16} />
              </button>
            </div>
            <form
              style={{ padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: '14px' }}
              onSubmit={(e) => {
                e.preventDefault();
                handleCreateEmployee(addForm);
              }}
            >
              {createError && (
                <div className="flex items-center gap-2" style={{ background: '#FEF0EA', color: '#7A200B', borderRadius: '16px', padding: '12px 16px', fontSize: '13.5px' }}>
                  <AlertCircle size={16} className="flex-shrink-0" />
                  {createError}
                </div>
              )}
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Email *</span>
                <input
                  type="email"
                  required
                  value={addForm.email}
                  onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                  className={inputClass}
                  placeholder="employe@exemple.com"
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Mot de passe *</span>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={addForm.password}
                  onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                  className={inputClass}
                  placeholder="••••••••"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Prénom *</span>
                  <input
                    type="text"
                    required
                    value={addForm.first_name}
                    onChange={(e) => setAddForm({ ...addForm, first_name: e.target.value })}
                    className={inputClass}
                    placeholder="Jean"
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Nom *</span>
                  <input
                    type="text"
                    required
                    value={addForm.last_name}
                    onChange={(e) => setAddForm({ ...addForm, last_name: e.target.value })}
                    className={inputClass}
                    placeholder="Dupont"
                  />
                </label>
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Téléphone</span>
                <input
                  type="tel"
                  value={addForm.phone}
                  onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
                  className={inputClass}
                  placeholder="06 12 34 56 78"
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Rôle *</span>
                <select
                  required
                  value={addForm.role}
                  onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}
                  className={inputClass}
                >
                  {roles.filter(r => r.id !== 'all').map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Date d'embauche *</span>
                <input
                  type="date"
                  required
                  value={addForm.hire_date}
                  onChange={(e) => setAddForm({ ...addForm, hire_date: e.target.value })}
                  className={inputClass}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Salaire (€, optionnel)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={addForm.salary}
                  onChange={(e) => setAddForm({ ...addForm, salary: e.target.value })}
                  className={inputClass}
                  placeholder="0"
                />
              </label>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => !creating && setShowAddModal(false)}
                  className="flex-1"
                  style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: '#054625', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', borderRadius: '999px' }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1"
                  style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', borderRadius: '999px', opacity: creating ? 0.6 : 1 }}
                >
                  {creating ? 'Création...' : "Créer l'employé"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Modifier un employé */}
      {editingEmployee && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(5,16,4,0.5)' }}>
          <div style={{ background: '#FCFBF5', borderRadius: '28px', maxWidth: '460px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(5,16,4,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '22px 26px', borderBottom: '1px solid rgba(5,16,4,0.08)' }}>
              <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>Modifier l'employé</h2>
              <button
                onClick={() => setEditingEmployee(null)}
                style={{ flexShrink: 0, border: 'none', cursor: 'pointer', width: '36px', height: '36px', borderRadius: '999px', background: 'transparent', color: 'rgba(5,16,4,0.4)', display: 'grid', placeItems: 'center' }}
              >
                <X size={16} />
              </button>
            </div>
            <form
              style={{ padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: '14px' }}
              onSubmit={(e) => {
                e.preventDefault();
                handleUpdateEmployee(editingEmployee.id, editForm);
              }}
            >
              <div className="grid grid-cols-2 gap-3">
                <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Prénom *</span>
                  <input
                    type="text"
                    required
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                    className={inputClass}
                    placeholder="Jean"
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Nom *</span>
                  <input
                    type="text"
                    required
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                    className={inputClass}
                    placeholder="Dupont"
                  />
                </label>
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Téléphone</span>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className={inputClass}
                  placeholder="06 12 34 56 78"
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Rôle *</span>
                <select
                  required
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className={inputClass}
                >
                  {roles.filter(r => r.id !== 'all').map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Date d'embauche *</span>
                <input
                  type="date"
                  required
                  value={editForm.hire_date}
                  onChange={(e) => setEditForm({ ...editForm, hire_date: e.target.value })}
                  className={inputClass}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: 'rgba(5,16,4,0.6)' }}>Salaire (€, optionnel)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.salary}
                  onChange={(e) => setEditForm({ ...editForm, salary: e.target.value })}
                  className={inputClass}
                  placeholder="0"
                />
              </label>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => !updating && setEditingEmployee(null)}
                  className="flex-1"
                  style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: '#054625', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', borderRadius: '999px' }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={updating}
                  className="flex-1"
                  style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', borderRadius: '999px', opacity: updating ? 0.6 : 1 }}
                >
                  {updating ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2" style={{ background: '#FEF0EA', color: '#7A200B', borderRadius: '18px', padding: '14px 18px', fontSize: '14px' }}>
          <AlertCircle size={18} />
          {error?.message || error?.toString() || 'Une erreur est survenue'}
        </div>
      )}

      {/* Onglets de statut */}
      <div className="flex flex-wrap gap-1" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '4px', width: 'fit-content' }}>
        {[
          { id: 'all', label: `Tous · ${employees.length}` },
          { id: 'active', label: `Actifs · ${activeCount}` },
          { id: 'inactive', label: `Inactifs · ${inactiveCount}` },
          { id: 'manager', label: `Managers · ${managerCount}` },
        ].map((tab) => {
          const active = statusFilter === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              style={{
                border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontSize: '13.5px', fontWeight: 600,
                padding: '9px 16px', borderRadius: '999px', whiteSpace: 'nowrap',
                background: active ? PRIMARY : 'transparent',
                color: active ? '#FBF9D9' : 'rgba(5,16,4,0.66)',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Filtres et recherche */}
      <div className="flex flex-col sm:flex-row gap-3">
        <label className="flex items-center gap-2.5 flex-1" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '0 18px', minHeight: '44px' }}>
          <Search size={15} style={{ color: PRIMARY, flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Rechercher un employé…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '14px', color: '#051004', outline: 'none' }}
          />
        </label>
        <div className="sm:w-56">
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="w-full"
            style={{ background: '#FCFBF5', border: 'none', borderRadius: '999px', padding: '0 18px', minHeight: '44px', fontSize: '14px', color: '#051004', outline: 'none' }}
          >
            {roles.map(role => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Liste des employés */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {paginatedEmployees.length === 0 && (
          <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '48px 16px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.5)' }}>Aucun employé trouvé pour ce filtre.</p>
          </div>
        )}
        {paginatedEmployees.map((employee) => {
          const roleBadge = ROLE_BADGE[employee.role] || ROLE_BADGE.staff;
          const statusBadge = STATUS_BADGE[employee.status] || STATUS_BADGE.inactive;
          const available = employee.status === 'active';
          const initials = employee.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '—';
          return (
            <div
              key={employee.id}
              className="flex items-center gap-3.5 flex-wrap"
              style={{ background: '#FCFBF5', borderRadius: '26px', padding: '14px 18px' }}
            >
              <span style={{ flex: 'none', width: '46px', height: '46px', borderRadius: '999px', background: '#F4F1E2', display: 'grid', placeItems: 'center', fontFamily: 'Archivo, sans-serif', fontSize: '13px', fontWeight: 700, color: PRIMARY }}>
                {initials}
              </span>

              <span style={{ flex: '1 1 200px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '16px' }} className="truncate">{employee.name}</strong>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.6)' }} className="truncate">{employee.email || '—'}</span>
              </span>

              <span className="flex items-center gap-1.5" style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.66)', whiteSpace: 'nowrap' }}>
                <Phone size={13} />
                {employee.phone || '—'}
              </span>

              <span style={{ background: roleBadge.bg, color: roleBadge.fg, fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, padding: '5px 12px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                {getRoleName(employee.role)}
              </span>

              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.6)', whiteSpace: 'nowrap' }}>
                {formatHireDate(employee.hireDate)}
              </span>

              <span style={{ background: statusBadge.bg, color: statusBadge.fg, fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, padding: '5px 12px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                {statusBadge.label}
              </span>

              <span className="flex items-center gap-1.5">
                <button
                  onClick={() => openEditModal(employee)}
                  className="w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.6)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#FBF9D9'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  title="Modifier"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => handleToggleEmployeeStatus(employee.id)}
                  className="w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.6)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#FBF9D9'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  title={available ? 'Désactiver' : 'Activer'}
                >
                  {available ? <UserX size={16} /> : <UserCheck size={16} />}
                </button>
                <button
                  onClick={() => window.confirm('Supprimer cet employé ?') && handleDeleteEmployee(employee.id)}
                  className="w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.6)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#FEF0EA'; e.currentTarget.style.color = '#A32C0F'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(5,16,4,0.6)'; }}
                  title="Supprimer"
                >
                  <Trash2 size={16} />
                </button>
              </span>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.6)' }}>
          Total employés : <strong style={{ color: '#051004' }}>{filteredEmployees.length}</strong>
        </span>
        <div className="flex items-center gap-2">
          <button
            style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', minHeight: '34px', padding: '0 16px', borderRadius: '999px', fontFamily: 'Archivo, sans-serif', fontSize: '13px', opacity: currentPageSafe === 1 ? 0.4 : 1 }}
            disabled={currentPageSafe === 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            ‹
          </button>
          {Array.from({ length: totalPages }).slice(0, 5).map((_, idx) => {
            const page = idx + 1;
            const active = page === currentPageSafe;
            return (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                style={{
                  minWidth: '34px', height: '34px', borderRadius: '999px', fontFamily: 'Archivo, sans-serif', fontSize: '13px', fontWeight: 700,
                  border: active ? 'none' : '1px solid rgba(5,16,4,0.16)', cursor: 'pointer',
                  background: active ? TERRA : 'transparent',
                  color: active ? '#FCFBF5' : '#051004',
                }}
              >
                {page}
              </button>
            );
          })}
          <button
            style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', minHeight: '34px', padding: '0 16px', borderRadius: '999px', fontFamily: 'Archivo, sans-serif', fontSize: '13px', opacity: currentPageSafe === totalPages ? 0.4 : 1 }}
            disabled={currentPageSafe === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestaurantEmployees;
